import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import isProductDisabled from 'garaje/helpers/product-disabled';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type { MessageType } from 'garaje/services/flash-messages';
import type LocalStorageService from 'garaje/services/local-storage';
import type MessageBusService from 'garaje/services/message-bus';
import type SigmaUrlService from 'garaje/services/sigma-url';
import type SkinnyLocationsService from 'garaje/services/skinny-locations';
import type StateService from 'garaje/services/state';
import type UserFeatureFlagsService from 'garaje/services/user-feature-flags';
import { Workbook } from 'garaje/utils/sigma-workbooks';
import urlBuilder from 'garaje/utils/url-builder';

type VisitorsGlobalMessage = {
  query?: {
    location_id?: string;
    company_id?: string;
    flow_id?: string;
    start?: string;
    end?: string;
  };
  label?: string;
  event:
    | 'showEntryExportModal'
    | 'showInviteExportModal'
    | 'closeExportModal'
    | 'showScheduleReportsModal'
    | 'showToaster'
    | 'closeScheduleReportsModal';
  type?: string; // optional, used when event is 'showToaster'
  msg?: string; // optional, used when event is 'showToaster'
};

export default class GlobalAnalyticsVisitorsController extends Controller {
  @service declare messageBus: MessageBusService;
  @service declare state: StateService;
  @service declare localStorage: LocalStorageService;
  @service declare flashMessages: FlashMessagesService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare sigmaUrl: SigmaUrlService;
  @service declare userFeatureFlags: UserFeatureFlagsService;
  @service declare skinnyLocations: SkinnyLocationsService;

  @tracked showModal = false;
  @tracked exportIframeUrl = '';
  @tracked isError = false;

  constructor(properties: Record<string, unknown>) {
    super(properties);
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.messageBus.on('embedded-app-message', this, this.handleMessage);
  }

  get canAccess(): boolean {
    if (isProductDisabled('analytics-v2')) return false;
    return (
      this.featureFlags.isEnabled('sigma-embedded-analytics') ||
      this.userFeatureFlags.isEnabled('sigma-embedded-analytics-by-user')
    );
  }

  get isBoss(): boolean {
    return this.localStorage.getItem('is_boss') === 'true';
  }

  get legacyIframeUrl(): string {
    const companyId = this.state?.currentCompany?.id;

    return urlBuilder.embeddedVisitorsGlobalAnalyticsUrl(companyId);
  }

  fetchSigmaUrlTask = task({ drop: true }, async () => {
    const companyId = this.state?.currentCompany?.id;
    try {
      const result = await this.sigmaUrl.getGlobalSigmaUrl(companyId, Workbook.GLOBAL_VISITORS);
      return result;
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', 'Error loading dashboard');
      this.isError = true;
      // eslint-disable-next-line no-console
      console.error(e);
      return;
    }
  });

  get sigmaIframeUrl(): string | null | undefined {
    return this.fetchSigmaUrlTask.last?.value;
  }

  get exportLocationIds(): string {
    const locations = this.skinnyLocations.currentCompanyLocations || [];
    return locations.map((loc) => loc.id).join(',');
  }

  get exportLabel(): string {
    const locations = this.skinnyLocations.currentCompanyLocations || [];
    return locations.length === 1 ? locations[0]?.name || 'All locations' : 'All locations';
  }

  @action
  toggleShowModal(): void {
    this.showModal = !this.showModal;
  }

  @action
  handleMessage(message: VisitorsGlobalMessage): void {
    const locationId = message.query?.location_id || '';
    const companyId = message.query?.company_id || '';
    const flowId = '';
    const start = message.query?.start || '';
    const end = message.query?.end || '';
    const label = message.label || '';

    // When feature flag is enabled, translate export events to new format
    if (this.featureFlags.isEnabled('analytics-improved-exports')) {
      if (message.event === 'showEntryExportModal') {
        this.messageBus.trigger('embedded-app-message', {
          event: 'showModal',
          exportType: 'visitors',
          query: message.query,
          label: message.label,
        });
        return;
      } else if (message.event === 'showInviteExportModal') {
        this.messageBus.trigger('embedded-app-message', {
          event: 'showModal',
          exportType: 'invites',
          query: message.query,
          label: message.label,
        });
        return;
      } else if (message.event === 'closeExportModal') {
        this.messageBus.trigger('embedded-app-message', {
          event: 'closeModal',
        });
        return;
      }
    }

    // Handle all events with old modal (scheduled reports always, exports when flag is off)
    if (message.event === 'showEntryExportModal') {
      this.exportIframeUrl = urlBuilder.embeddedEntryExportModalUrl(companyId, locationId, flowId, start, end, label);
      this.toggleShowModal();
    } else if (message.event === 'showInviteExportModal') {
      this.exportIframeUrl = urlBuilder.embeddedInviteExportModalUrl(companyId, locationId, flowId, start, end, label);
      this.toggleShowModal();
    } else if (message.event === 'showScheduleReportsModal') {
      this.exportIframeUrl = urlBuilder.embeddedScheduleReportsModalUrl(companyId, locationId, flowId, start, end);
      this.toggleShowModal();
    } else if (message.event === 'closeExportModal' || message.event === 'closeScheduleReportsModal') {
      this.toggleShowModal();
    } else if (message.event === 'showToaster') {
      const type = message.type as MessageType;
      const msg = message.msg || '';
      this.flashMessages.showAndHideFlash(type, msg);
    }
  }
}
